import React from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { Field, useFormikContext } from "formik";
import { TextField } from "formik-mui";
import * as yup from "yup";
import { StateDropdown } from "@app.automotus.io/components/StateDropdownField";
import { LicensePlateState, StateAbbreviation } from "@app.automotus.io/components/common/LicensePlateState";
import { LicensePlateField } from "@app.automotus.io/components/forms/VehicleInfoFields/LicensePlateField";

export interface VehicleValues {
  licensePlate: string;
  licensePlateState: StateAbbreviation | null;
  vehicleMake: string;
  vehicleModel: string;
  vehicleColor: string;
}

/** Normalizes a stored state code (e.g. the jurisdiction's address_state) to a dropdown value, or null if unrecognized. */
export const toLicensePlateState = (value: string | null | undefined): StateAbbreviation | null => {
  const abbreviation = value?.trim().toUpperCase();
  return abbreviation && abbreviation in LicensePlateState.ByAbbreviation ? (abbreviation as StateAbbreviation) : null;
};

export interface VehicleComponent extends React.FC {
  defaultInitialValues: VehicleValues;
  validationSchema: yup.AnyObjectSchema;
}

export const Vehicle: VehicleComponent = () => {
  const formik = useFormikContext<VehicleValues>();

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Vehicle
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <LicensePlateField />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StateDropdown
            fullWidth
            required
            name="licensePlateState"
            label="License Plate State"
            placeholder="Select State"
            value={formik.values.licensePlateState}
            onChange={(_, value) => formik.setFieldValue("licensePlateState", value)}
            onBlur={formik.handleBlur}
            error={formik.touched.licensePlateState && Boolean(formik.errors.licensePlateState)}
            helperText={
              formik.touched.licensePlateState && formik.errors.licensePlateState
                ? String(formik.errors.licensePlateState)
                : ""
            }
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Field component={TextField} fullWidth name="vehicleMake" label="Make" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Field component={TextField} fullWidth name="vehicleModel" label="Model" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Field component={TextField} fullWidth name="vehicleColor" label="Color" />
        </Grid>
      </Grid>
    </Paper>
  );
};

Vehicle.defaultInitialValues = {
  licensePlate: LicensePlateField.defaultInitialValue,
  licensePlateState: null,
  vehicleMake: "",
  vehicleModel: "",
  vehicleColor: "",
};

Vehicle.validationSchema = yup.object({
  licensePlate: LicensePlateField.validationSchema,
  licensePlateState: yup.mixed<StateAbbreviation>().required("State is required"),
  vehicleMake: yup.string(),
  vehicleModel: yup.string(),
  vehicleColor: yup.string(),
});

export default Vehicle;
